import type { Book, Chapter, LibrarySource, ProgressFile, Track } from '../types';
import { EMPTY_PROGRESS } from '../types';
import { bookChapters } from '../lib/mediaInfo';

/**
 * Demo library — a LibrarySource backed by the generated files in
 * public/demo-audio, deliberately mirroring the two layouts the real scanner
 * has to cope with: a folder of per-chapter files, and a single long file with
 * embedded chapter marks. A few books sit inside a "Short Stories" folder, so
 * the library's folder tiles can be tried as well.
 *
 * The "Embedded chapters" folder holds an M4B, a FLAC and an MP3 that carry
 * their own chapters and cover art, given here as neither: both are read out
 * of the files over HTTP range requests, by exactly the code Drive uses.
 *
 * It exists so the whole app — library, player, chapter logic, sleep timer and
 * the cross-device progress merge — can be run and tested without a Google
 * account. Its "remote" progress lives in localStorage under a distinct key,
 * standing in for the appDataFolder file, so the sync/merge path is exercised
 * for real rather than short-circuited.
 */

const REMOTE_KEY = 'shelf.demo.remoteProgress';

function demoUrl(file: string): string {
  return `${import.meta.env.BASE_URL}demo-audio/${file}`;
}

/** Byte size of an 8-bit 8 kHz mono WAV of the given length, matching what
 *  scripts/make-demo-audio.mjs writes. */
const wavBytes = (seconds: number) => Math.round(seconds * 8000) + 44;

/** No `durationSec`, exactly as a Drive scan returns: lengths are learned by
 *  playing or measuring, so demo mode exercises that path too. */
function track(id: string, name: string, file: string, seconds: number, mimeType = 'audio/wav'): Track {
  return {
    id,
    name,
    fileName: file,
    mimeType,
    sizeBytes: wavBytes(seconds),
  };
}

/** File sizes as written by scripts/make-demo-audio.mjs — the sizes Drive
 *  would report. Range reads depend on them being exact. */
async function loadSizes(): Promise<Record<string, number>> {
  try {
    const response = await fetch(demoUrl('manifest.json'));
    return response.ok ? ((await response.json()) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** A soft gradient cover, inline so demo mode needs no image assets. */
function cover(title: string, author: string, from: string, to: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>
    </linearGradient>
    <radialGradient id="v" cx="0.3" cy="0.2" r="0.9">
      <stop offset="0" stop-color="#fff" stop-opacity="0.28"/>
      <stop offset="1" stop-color="#fff" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="400" height="400" fill="url(#g)"/>
  <rect width="400" height="400" fill="url(#v)"/>
  <text x="200" y="196" text-anchor="middle" fill="#fff" font-family="'Palatino Linotype', Palatino, Georgia, serif"
        font-size="34" font-weight="700" opacity="0.96">${title}</text>
  <text x="200" y="232" text-anchor="middle" fill="#fff" font-family="'Palatino Linotype', Palatino, Georgia, serif"
        font-size="19" font-weight="500" opacity="0.75">${author}</text>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const SHORT_STORIES = { id: 'demo-folder-stories', name: 'Short Stories' };
const EMBEDDED = { id: 'demo-folder-embedded', name: 'Embedded chapters' };

const BOOKS: Book[] = [
  {
    id: 'demo-willows',
    title: 'The Wind in the Willows',
    author: 'Kenneth Grahame',
    kind: 'folder',
    coverUrl: cover('Willows', 'Kenneth Grahame', '#4c6ef5', '#7048e8'),
    tracks: [
      track('demo-willows-1', 'The River Bank', 'willows-01.wav', 42),
      track('demo-willows-2', 'The Open Road', 'willows-02.wav', 58),
      track('demo-willows-3', 'The Wild Wood', 'willows-03.wav', 36),
    ],
    totalSizeBytes: wavBytes(42 + 58 + 36),
  },
  {
    id: 'demo-meditations',
    title: 'Meditations',
    author: 'Marcus Aurelius',
    kind: 'single',
    coverUrl: cover('Meditations', 'Marcus Aurelius', '#0b7285', '#1098ad'),
    tracks: [track('demo-meditations-1', 'Meditations', 'meditations.wav', 180)],
    totalSizeBytes: wavBytes(180),
  },
  {
    // No cover on purpose: exercises the generated-initials fallback tile.
    id: 'demo-notes',
    title: 'Notes on Nothing in Particular',
    author: 'Anonymous',
    kind: 'single',
    tracks: [track('demo-notes-1', 'Notes on Nothing in Particular', 'willows-03.wav', 36)],
    totalSizeBytes: wavBytes(36),
    folderPath: [SHORT_STORIES],
  },
  {
    id: 'demo-magi',
    title: 'The Gift of the Magi',
    author: 'O. Henry',
    kind: 'single',
    coverUrl: cover('The Magi', 'O. Henry', '#c2255c', '#e8590c'),
    tracks: [track('demo-magi-1', 'The Gift of the Magi', 'willows-01.wav', 42)],
    totalSizeBytes: wavBytes(42),
    folderPath: [SHORT_STORIES],
  },
  {
    id: 'demo-heart',
    title: 'The Tell-Tale Heart',
    author: 'Edgar Allan Poe',
    kind: 'single',
    tracks: [track('demo-heart-1', 'The Tell-Tale Heart', 'willows-02.wav', 58)],
    totalSizeBytes: wavBytes(58),
    folderPath: [SHORT_STORIES],
  },
  // No cover and no chapters given: both come out of the files.
  {
    id: 'demo-m4b',
    title: 'Harbour Lights',
    author: 'M4B, chapter track',
    kind: 'single',
    tracks: [track('demo-m4b-1', 'Harbour Lights', 'harbour-lights.m4b', 96, 'audio/mp4')],
    totalSizeBytes: 0,
    folderPath: [EMBEDDED],
  },
  {
    id: 'demo-flac',
    title: 'Four Movements',
    author: 'FLAC, chapter tags',
    kind: 'single',
    tracks: [track('demo-flac-1', 'Four Movements', 'four-movements.flac', 80, 'audio/flac')],
    totalSizeBytes: 0,
    folderPath: [EMBEDDED],
  },
  {
    id: 'demo-mp3',
    title: 'Silence, in Three Parts',
    author: 'MP3, ID3 chapters (silent)',
    kind: 'single',
    tracks: [track('demo-mp3-1', 'Silence, in Three Parts', 'three-parts.mp3', 60, 'audio/mpeg')],
    totalSizeBytes: 0,
    folderPath: [EMBEDDED],
  },
];

/** Chapter marks for the single-file book, as a real .m4b parse would yield. */
const CHAPTERS: Record<string, Chapter[]> = {
  'demo-meditations': [
    { title: 'Book I — Debts and Lessons', trackIndex: 0, startSec: 0 },
    { title: 'Book II — On the River Gran', trackIndex: 0, startSec: 45 },
    { title: 'Book III — In Carnuntum', trackIndex: 0, startSec: 90 },
    { title: 'Book IV — The Inner Citadel', trackIndex: 0, startSec: 135 },
  ],
};

export class DemoSource implements LibrarySource {
  readonly kind = 'demo' as const;
  readonly label = 'Demo library';

  async listBooks(): Promise<Book[]> {
    // A beat of latency so loading states are visible and testable.
    await new Promise((r) => setTimeout(r, 180));
    const sizes = await loadSizes();
    return BOOKS.map((b) => {
      const tracks = b.tracks.map((t) => ({ ...t, sizeBytes: sizes[t.fileName] ?? t.sizeBytes }));
      return { ...b, tracks, totalSizeBytes: tracks.reduce((n, t) => n + t.sizeBytes, 0) };
    });
  }

  trackUrl(track: Track): string {
    return demoUrl(track.fileName);
  }

  metadataUrl(track: Track): string {
    return demoUrl(track.fileName);
  }

  async readRange(track: Track, start: number, end: number): Promise<ArrayBuffer> {
    const response = await fetch(demoUrl(track.fileName), {
      headers: { Range: `bytes=${start}-${end}` },
    });
    if (!response.ok) throw new Error(`Demo file ${track.fileName}: ${response.status}`);
    const body = await response.arrayBuffer();
    // A server that ignores Range sends the whole file.
    return response.status === 206 ? body : body.slice(start, end + 1);
  }

  async chaptersFor(book: Book): Promise<Chapter[]> {
    const explicit = CHAPTERS[book.id];
    if (explicit) return explicit;
    // Everything else goes through the same reader as Drive: embedded
    // chapters where a file has them, one chapter per file where not.
    return bookChapters(book, (track, start, end) => this.readRange(track, start, end));
  }

  async loadProgress(): Promise<ProgressFile | null> {
    await new Promise((r) => setTimeout(r, 120));
    const raw = localStorage.getItem(REMOTE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as ProgressFile;
      return parsed?.books ? parsed : EMPTY_PROGRESS;
    } catch {
      return EMPTY_PROGRESS;
    }
  }

  async saveProgress(progress: ProgressFile): Promise<void> {
    await new Promise((r) => setTimeout(r, 80));
    localStorage.setItem(REMOTE_KEY, JSON.stringify(progress));
  }
}
